/* eslint-disable react/prop-types */

import pick from 'lodash/pick';

import {
  formatData,
  getThresholdTier,
  isLowSampleTier,
  isSuppressedTier,
  isValueColumnFormat,
  NON_ROBUST_TEXT_COLOR,
} from '@utilities';

import IndexBar from '../components/IndexBar';

const resolveAccessor = ({ accessor, row }) => {
  let index = 0;
  let value;

  for (const [keyIndex, key] of accessor.split(';').entries()) {
    if (row?.[key] === 'NAN_SENTINEL') break;
    index = keyIndex;
    value = row?.[key];
  }

  return { index, value };
};

const getColumnValue = ({ accessor, row }) => resolveAccessor({ accessor, row }).value;

/**
 * gets table data rows and columns
 * @param  {Object} options.activeColumnFilter
 * @param  {Node}   options.children
 * @param  {string} options.defaultDataId
 * @param  {Object} options.filteredData
 * @return {Array|Array} columns and rows
 */
export const getTableData = ({ activeColumnFilter, children, defaultDataId, filteredData }) => {
  const tableSet = defaultDataId
    ? filteredData.find((set) => set?.id === defaultDataId)
    : filteredData[0];

  if (!tableSet) return {};

  const getColumns = (columns) =>
    columns
      .filter((child) => child?.props?.data)
      .map((column) => {
        const hasIndexBar = column?.props?.addChart === 'True';
        const width = parseInt(column?.props?.width);
        const accessor = column?.props?.data?.replace('.', '_');

        return {
          accessorFn: (row) => getColumnValue({ accessor, row }),
          accessorKey: accessor,
          cell: (props) => {
            const accessors = accessor.split(';');
            const { index, value } = resolveAccessor({ accessor, row: props?.row?.original });

            const format = column?.props?.format;
            // Only value (metric) columns carry a sample tier; dimension/label
            // columns (no numeric format) are left untouched.
            const isValueColumn = isValueColumnFormat(format);
            const tier = isValueColumn
              ? getThresholdTier(props?.row?.original, accessors[index])
              : undefined;
            const suppressed = isSuppressedTier(tier);
            const lowSample = isLowSampleTier(tier);

            const style = hasIndexBar
              ? {
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }
              : {};

            return (
              <span
                style={{
                  ...style,
                  ...{
                    marginLeft: `${index * 15}px`,
                  },
                  ...((suppressed || lowSample) && { color: NON_ROBUST_TEXT_COLOR }),
                }}
              >
                {formatData({
                  format,
                  value: suppressed ? 'NAN_SENTINEL' : (value ?? 'NAN_SENTINEL'),
                })}
                {hasIndexBar && !suppressed && <IndexBar value={value} />}
              </span>
            );
          },
          disableSortBy: column?.props?.disableSorting === 'true',
          format: column?.props?.format,
          getCellHighlight: ({ rowIndex, value }) => {
            if (
              tableSet?.metadata?.summaryRows &&
              tableSet?.metadata?.summaryRows.includes(rowIndex) &&
              !tableSet?.metadata?.indentedRows[rowIndex]
            ) {
              return 'header';
            }

            if (
              !value ||
              column?.props?.heatMap !== 'True' ||
              !column?.props?.data.includes(column?.props?.heatMapColumn)
            )
              return;

            if (value <= 80) {
              return 'negative';
            } else if (value <= 90 && value > 80) {
              return 'soft-negative';
            } else if (value >= 110 && value < 120) {
              return 'soft-positive';
            } else if (value >= 120) {
              return 'positive';
            }
          },
          gradient: column?.props?.gradient === 'True',
          Header: tableSet?.colLabels[column?.props?.data] || '',
          headerTooltip: column?.props?.tooltip,
          width: width >= 150 ? width : 150,
        };
      })
      .filter((column) => {
        return !activeColumnFilter
          ? column
          : activeColumnFilter?.value?.target.includes(column?.accessorKey) ||
              activeColumnFilter?.value?.target.includes(column?.accessorKey.replace('_', '.'));
      });

  const columnGroups = children.filter((child) => Array.isArray(child?.props?.children));

  const columns =
    columnGroups?.length > 0
      ? children
          .filter((child) => Array.isArray(child?.props?.children) || child?.props?.data)
          .map((child, index) => {
            const isColumnGroup = Array.isArray(child?.props?.children);

            return isColumnGroup
              ? {
                  columns: getColumns(child?.props?.children),
                  header: child?.props?.title,
                  id: child?.props?.title,
                }
              : {
                  columns: getColumns([child]),
                  header: '',
                  id: `column-group-${index}`,
                };
          })
      : getColumns(children);

  const rows = tableSet?.data.map((set) => {
    Object.keys(set).forEach((setKey) => {
      if (setKey.includes('.')) {
        const newKey = setKey.replace('.', '_');
        set[newKey] = set[setKey];
        delete set[setKey];
      }
    });

    return !activeColumnFilter
      ? set
      : {
          ...set,
          data: set?.data?.map((row) => pick(row, activeColumnFilter?.value?.target)),
        };
  });

  // Suppress the underlying value for tier-0/null cells, not just at render, so
  // every consumer that reads the raw value — column sorting, heatmap/gradient
  // backgrounds, Copy Table — treats a suppressed cell as missing ("-") rather
  // than leaking its hidden sub-threshold magnitude. Low-sample (tier 1) values
  // are left intact (shown in gray). No-op for non-Worldpanel tables (no tier
  // keys).
  //
  // Rows are shallow-cloned before suppression so we never mutate the shared
  // source dataset: sibling chart/tree/export builders in the same report read
  // the same `filteredData` row objects and must still see the real values.
  const leafColumns = columns.flatMap((column) =>
    Array.isArray(column?.columns) ? column.columns : [column]
  );
  const suppressedRows = rows.map((row) => {
    if (!row) return row;
    let suppressedRow = row;
    leafColumns.forEach((column) => {
      if (!isValueColumnFormat(column?.format)) return;
      (column?.accessorKey?.split(';') || []).forEach((accessor) => {
        if (isSuppressedTier(getThresholdTier(row, accessor))) {
          if (suppressedRow === row) suppressedRow = { ...row };
          suppressedRow[accessor] = 'NAN_SENTINEL';
        }
      });
    });
    return suppressedRow;
  });

  return { columns, rows: suppressedRows };
};
